/**
 * main.tsx — Admin app entry point (D-01: distinct build).
 *
 * Provider stack (outer→inner):
 *   QueryClientProvider → ToastProvider → WsProvider → RouterProvider
 *
 * Admin-gated: GET /auth/me is fetched once on mount; role≠admin → /login.
 * All timestamps use Europe/Berlin (D-09) via lib/time.ts formatters.
 */

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ToastProvider, ThemeProvider, ThemeSwitch, useSessionKeepalive } from '@crm/ui';
import { WsProvider } from './lib/WsProvider.js';
import { authApi } from './lib/api-client.js';
import { Login } from './pages/Login.js';
import { Onboarding } from './pages/Onboarding.js';
import { Accounts } from './pages/Accounts.js';
import { PostBot } from './pages/PostBot.js';
import { PostBotAccount } from './pages/PostBotAccount.js';
import { Assignments } from './pages/Assignments.js';
import { AuditLog } from './pages/AuditLog.js';
import { KpiDashboard } from './features/kpi/KpiDashboard.js';
import { Users } from './pages/Users.js';
import { AccountSettings } from './pages/AccountSettings.js';
import { AcceptInvite } from './pages/AcceptInvite.js';
import { ResetPassword } from './pages/ResetPassword.js';
import { BroadcastsPage } from './pages/BroadcastsPage.js';
import { Chats } from './pages/Chats.js';
import { SyncStalenessBanner } from './components/SyncStalenessBanner.js';
import './index.css';

// ---------------------------------------------------------------------------
// TanStack Query client
// ---------------------------------------------------------------------------

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

// ---------------------------------------------------------------------------
// Auth guard — fetches /auth/me; redirects to /login if not admin
// ---------------------------------------------------------------------------

function AdminGuard() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['me'],
    queryFn: () => authApi.me(),
    retry: false,
  });

  React.useEffect(() => {
    if (!isLoading && (isError || (data && data.role !== 'admin'))) {
      navigate('/login', { replace: true });
    }
  }, [isLoading, isError, data, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <span className="text-text-muted text-sm">Loading…</span>
      </div>
    );
  }

  if (isError || !data || data.role !== 'admin') return null;

  return (
    <WsProvider>
      <AdminShell />
    </WsProvider>
  );
}

// ---------------------------------------------------------------------------
// Admin shell — nav + content area
// ---------------------------------------------------------------------------

function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQuery({ queryKey: ['me'], queryFn: () => authApi.me() });

  // The Chats surface (@crm/chat's ChatWorkspace) is a full-bleed, self-scrolling
  // three-column layout: its internal message thread owns the only scroll region and
  // relies on `h-full` resolving to a DEFINITE bounded height (so it can snap to the
  // newest message on open). Every other admin page is ordinary document flow that
  // wants the padded, scrollable `main`. So the shell gives `main` a definite viewport
  // height (h-dvh + min-h-0) and, for /chats only, drops the padding + outer scroll so
  // the thread — not the page — is what scrolls (chatter-app parity; owner 2026-07-19).
  const isChats =
    location.pathname === '/chats' || location.pathname.startsWith('/chats/');

  // Sliding session: rotate the session cookie while the tab is in use so the
  // server TTL acts as an idle timeout, not a mid-shift logout.
  useSessionKeepalive(authApi.refresh);

  const handleLogout = async () => {
    await authApi.logout().catch(() => {});
    queryClient.clear();
    navigate('/login', { replace: true });
  };

  return (
    <div className="h-dvh bg-bg text-text flex flex-col overflow-hidden">
      {/* Top nav */}
      <header className="border-b border-border px-4 py-2 flex flex-wrap items-center gap-4 bg-bg-card">
        <span className="text-sm font-bold text-accent tracking-wide">
          MaloumDash
        </span>
        <span className="text-text-subtle text-xs">Admin</span>
        <nav className="flex flex-wrap gap-1 ml-0 sm:ml-4 order-3 w-full sm:order-none sm:w-auto">
          {([
            { label: 'Chats', to: '/chats' },
            { label: 'Onboarding', to: '/onboarding' },
            { label: 'Accounts', to: '/accounts' },
            { label: 'Assignments', to: '/assignments' },
            { label: 'Users', to: '/users' },
            { label: 'Revenue', to: '/revenue' },
            { label: 'Post bot', to: '/post-bot' },
          ] as const).map(({ label, to }) => (
            <NavLink key={to} to={to} label={label} navigate={navigate} />
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <ThemeSwitch />
          {qc.data && (
            <span className="text-xs text-text-muted">{qc.data.email || qc.data.id}</span>
          )}
          <button
            onClick={() => navigate('/settings')}
            className="text-xs text-text-muted hover:text-text transition-colors"
          >
            Settings
          </button>
          <button
            onClick={handleLogout}
            className="text-xs text-text-muted hover:text-text transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Fleet sync alarm — renders only when NOTHING in the fleet is reaching Maloum. Sits
          directly under the nav so it is on screen on every admin page (2026-08-21 outage). */}
      <SyncStalenessBanner />

      {/* Main content — /chats is full-bleed + self-scrolling; other pages are padded + scrollable. */}
      <main className={isChats ? 'flex-1 min-h-0 overflow-hidden' : 'flex-1 min-h-0 overflow-auto p-4'}>
        <Outlet />
      </main>
    </div>
  );
}

function NavLink({
  to,
  label,
  navigate,
}: {
  to: string;
  label: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const isActive = window.location.pathname.startsWith(to);
  return (
    <button
      onClick={() => navigate(to)}
      className={[
        'px-2.5 py-1.5 text-xs rounded font-medium transition-colors',
        isActive
          ? 'bg-accent/20 text-accent'
          : 'text-text-muted hover:text-text hover:bg-bg-hover',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
  },
  // Public routes (outside AdminGuard)
  {
    path: '/accept-invite',
    element: <AcceptInvite />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/',
    element: <AdminGuard />,
    children: [
      { index: true, element: <Navigate to="/onboarding" replace /> },
      // Three flat routes (not a single "chats/:creatorId?/c/:conversationId?" path) —
      // react-router v7's matcher does not treat a mid-path dynamic segment as optional;
      // a single pattern string only matches when every param is present, which would
      // 404 the bare "/chats" landing route. Mirrors chatter-app's proven working
      // pattern: "/inbox", "/inbox/:creatorId", "/inbox/:creatorId/c/:conversationId".
      { path: 'chats', element: <Chats /> },
      { path: 'chats/:creatorId', element: <Chats /> },
      { path: 'chats/:creatorId/c/:conversationId', element: <Chats /> },
      { path: 'onboarding', element: <Onboarding /> },
      { path: 'accounts', element: <Accounts /> },
      { path: 'post-bot', element: <PostBot /> },
      { path: 'post-bot/:accountId', element: <PostBotAccount /> },
      { path: 'assignments', element: <Assignments /> },
      { path: 'users', element: <Users /> },
      { path: 'settings', element: <AccountSettings /> },
      { path: 'revenue', element: <KpiDashboard /> },
      { path: 'broadcasts', element: <BroadcastsPage /> },
      { path: 'audit', element: <AuditLog /> },
    ],
  },
  {
    // Catch-all → login
    path: '*',
    element: <Navigate to="/login" replace />,
  },
]);

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element');

createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
